import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const PetSittingFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersWithMinResponseTimeIn60minPct,
    sittersCanProvideDailyExercisePct,
    sittersCanProvideMedicationPct,
    sittersCanProvideSpecialCarePct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="pet-sitting-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Pet Sitting FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a pet sitter in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a pet sitter in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} pet sitter?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Pet sitters on Rover are free to set their rates. The median cost to hire a
                        pet sitter in {cityName} on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A pet sitter’s rate may also change as you customize your booking to fit
                        your and your pet’s needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Pet sitters on Rover are free to set their rates. The median cost to hire a
                        pet sitter in {neighborhoodName} on Rover as of {yearMonthLastUpdated} was
                        about {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A pet sitter’s rate may also change as you customize your booking to fit
                        your and your pet’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Pet sitters on Rover are free to set their rates. The median cost to hire a
                        {locationName} pet sitter on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A pet sitter’s rate may also change as you customize your booking to fit
                        your and your pet’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 2 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion question={t`How do I contact a ${locationName} pet sitter?`}>
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} pet sitter for the first time, visit
                    the pet sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a pet sitter before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 3 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: petSittersCount } = maxSearchableSittersNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many pet sitters are in ${cityName}?`,
                  neighborhood: `How many pet sitters are in ${neighborhoodName}?`,
                  other: `How many ${locationName} pet sitters are there?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {petSittersCount} pet sitters in{' '}
                        {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect pet sitter near you. As a reminder, pet
                        sitters joining Rover must pass a background check for you and your pet’s
                        safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {petSittersCount} pet sitters in{' '}
                        {neighborhoodName}. You can filter, sort, expand your radius, read reviews,
                        and compare pricing to find the perfect pet sitter near you. As a reminder,
                        pet sitters joining Rover must pass a background check for you and your
                        pet’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {petSittersCount} {locationName} pet
                        sitters. You can filter, sort, expand your radius, read reviews, and compare
                        pricing to find the perfect pet sitter near you. As a reminder, pet sitters
                        joining Rover must pass a background check for you and your pet’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a pet sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a pet sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} pet sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple pet sitters about your booking.
                    Typically, {percent} of {locationName} pet sitters respond in under an hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Rover makes it easy to reach out to multiple pet sitters about your booking.
                Typically, pet sitters on Rover respond in under an hour.
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are pet sitters in ${cityName}?`,
            neighborhood: `How experienced are pet sitters in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} pet sitters?`,
          })}
        >
          {(() => {
            if (yearsOfExperienceNum && avgRepeatCustomerAmongSittersWithRepeatNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Pet sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. Pet sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from pet sitter
                        to pet sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare pet sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Pet sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. Pet sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from pet sitter
                        to pet sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare pet sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} pet sitters have an average of {yearsOfExperience} years of
                        experience. Pet sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from pet sitter
                        to pet sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare pet sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (avgRepeatCustomerAmongSittersWithRepeatNum) {
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Pet sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from pet sitter to pet sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare pet sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Pet sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from pet sitter to pet sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare pet sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Pet sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from pet sitter to pet sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare pet sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (yearsOfExperienceNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Pet sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. This can vary widely from pet sitter to pet sitter, but you can
                        view reviews, years of experience, and number of repeat clients when
                        comparing pet sitters available in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Pet sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. This can vary widely from pet sitter to pet sitter, but
                        you can view reviews, years of experience, and number of repeat clients when
                        comparing pet sitters available in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} pet sitters have an average of {yearsOfExperience} years of
                        experience. This can vary widely from pet sitter to pet sitter, but you can
                        view reviews, years of experience, and number of repeat clients when
                        comparing pet sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            return (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from pet sitter to pet sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing pet
                      sitters available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from pet sitter to pet sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing pet
                      sitters available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from pet sitter to pet sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing pet
                      sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            );
          })()}
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of services do pet sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of services do pet sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of services do ${locationName} pet sitters offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Rover makes it easy to find {locationName} pet sitters to provide loving care from
                their own home. The background-checked, 5-star pet sitters you can find on Rover
                will welcome your pet into their home when you’re away—whether it’s just for the
                weekend or longer. Pet sitting is great for:
              </Trans>
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pets of all ages and abilities, including puppies
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pet parents looking for a safe, loving alternative to a kennel
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pets who’d love to socialize with their pet sitter’s pets
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        {maxSearchableSittersNum &&
          (() => {
            const petSittersCount = maxSearchableSittersNum.asNumber;
            return (
              <FrequentlyAskedQuestion
                question={t`My pet has special needs. Can I find pet sitting on Rover that can accommodate my pet’s needs?`}
              >
                {sittersCanProvideDailyExercisePct &&
                sittersCanProvideMedicationPct &&
                sittersCanProvideSpecialCarePct ? (
                  (() => {
                    const dailyExercisePercent = sittersCanProvideDailyExercisePct.asPercent;
                    const provideMedicationPercent = sittersCanProvideMedicationPct.asPercent;
                    const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                    return (
                      <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                        <Select
                          value={localityType}
                          _city={
                            <Trans>
                              There are {petSittersCount} pet sitters in {cityName} on Rover, so
                              you’re sure to find the perfect match for your pet! In {cityName},{' '}
                              {dailyExercisePercent} of pet sitters offer daily exercise,{' '}
                              {provideMedicationPercent} offer medication administration, and{' '}
                              {specialCarePercent} have experience with senior pets and pets with
                              special needs. We know each pet is unique and may have specific care
                              instructions. You can filter, sort, expand your radius, read reviews,
                              and compare pricing to find the perfect pet sitter near you. As a
                              reminder, pet sitters joining Rover must pass background checks for
                              you and your pet’s safety.
                            </Trans>
                          }
                          _neighborhood={
                            <Trans>
                              There are {petSittersCount} pet sitters in {neighborhoodName} on
                              Rover, so you’re sure to find the perfect match for your pet! In{' '}
                              {neighborhoodName}, {dailyExercisePercent} of pet sitters offer daily
                              exercise, {provideMedicationPercent} offer medication administration,
                              and {specialCarePercent} have experience with senior pets and pets
                              with special needs. We know each pet is unique and may have specific
                              care instructions. You can filter, sort, expand your radius, read
                              reviews, and compare pricing to find the perfect pet sitter near you.
                              As a reminder, pet sitters joining Rover must pass background checks
                              for you and your pet’s safety.
                            </Trans>
                          }
                          other={
                            <Trans>
                              There are {petSittersCount} {locationName} pet sitters on Rover, so
                              you’re sure to find the perfect match for your pet!{' '}
                              {dailyExercisePercent} of {locationName} pet sitters offer daily
                              exercise, {provideMedicationPercent} offer medication administration,
                              and {specialCarePercent} have experience with senior pets and pets
                              with special needs. We know each pet is unique and may have specific
                              care instructions. You can filter, sort, expand your radius, read
                              reviews, and compare pricing to find the perfect pet sitter near you.
                              As a reminder, pet sitters joining Rover must pass background checks
                              for you and your pet’s safety.
                            </Trans>
                          }
                        />
                      </Paragraph>
                    );
                  })()
                ) : (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          There are {petSittersCount} pet sitters in {cityName} on Rover, so you’re
                          sure to find the perfect match for your pet! We understand that each pet
                          is unique and may require specific instructions. You can filter, sort,
                          expand your radius, read reviews, and compare pricing to find the perfect
                          pet sitter near you. As a reminder, all pet sitters on Rover have been
                          background checked and screened for you and your pet’s safety.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          There are {petSittersCount} pet sitters in {neighborhoodName} on Rover, so
                          you’re sure to find the perfect match for your pet! We understand that
                          each pet is unique and may require specific instructions. You can filter,
                          sort, expand your radius, read reviews, and compare pricing to find the
                          perfect pet sitter near you. As a reminder, all pet sitters on Rover have
                          been background checked and screened for you and your pet’s safety.
                        </Trans>
                      }
                      other={
                        <Trans>
                          There are {petSittersCount} {locationName} pet sitters on Rover, so you’re
                          sure to find the perfect match for your pet! We understand that each pet
                          is unique and may require specific instructions. You can filter, sort,
                          expand your radius, read reviews, and compare pricing to find the perfect
                          pet sitter near you. As a reminder, all pet sitters on Rover have been
                          background checked and screened for you and your pet’s safety.
                        </Trans>
                      }
                    />
                  </Paragraph>
                )}
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do pet sitters in ${cityName} pass a background check?`,
            neighborhood: `Do pet sitters in ${neighborhoodName} pass a background check?`,
            other: `Do ${locationName} pet sitters pass a background check?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your pet sitter through messages on
              Rover for delightful photo updates. The Rover team is available 24/7, and your pet
              sitter has access to advice from qualified veterinary professionals. In the rare event
              of any issues during a booking, rest easy knowing your pet is covered by the Rover
              Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I cancel a pet sitting service and receive a refund?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your pet sitter requested to cancel this booking,
                      then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your pet service.
                    You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default PetSittingFaqs;
